import type { FC } from 'react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Route,
  Switch,
  useLocation,
  useParams,
  useRouteMatch
} from '@compat/router';
import Breadcrumbs from '@components/Breadcrumbs';
import Carousel from '@components/Carousel/Products';
import CatalogMain from './components/CatalogMain';
import CatalogCategory from './components/CatalogCategory';
import HorizontalRecipe from './components/HorizontalRecipe';
import { SeoBottomTextWrapper } from '@components/SeoWrapper';
import { getURLProps } from '@utils/setPagesUrl';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  bannerAndTextQueryOptions,
  buildBannerAndTextKeyParams,
  catalogFaqQueryOptions,
  catalogProductsInfiniteQueryOptions,
  popularMainPageItemsQueryOptions,
  recipesRecommendQueryOptions
} from '@queries/index';
import { buildInitialCatalogQueryArgs } from './components/CatalogCategory/catalogInitialParams';
import scrollTo from '@utils/scrollTo';
import { observer } from 'mobx-react';
import CatalogBar from './components/CatalogBar';
import type { BreadCrumbsT } from '@routes/ProductPage/index.type';
import type { CatalogTypeFiltersModel } from '@schema/cart';
import usePreviousValue from '@customHooks/usePreviousValue';
import type { CatalogSeoBlockProps } from './components/CatalogSeoBlock';
import CatalogSeoBlock from './components/CatalogSeoBlock';
import CatalogFaq from './components/CatalogFaq';
import { selectCatalogFaqItems } from './components/CatalogFaq/selectItems';
import { useStores } from '@root/src/stores';
import { twcls } from '@utils/twcls';
import { useCatalogMenu } from './useCatalogMenu';
import './component.scss';

const Catalog: FC = observer(() => {
  const { userStore, xStore } = useStores();
  const { id } = useParams<{
    id: string;
  }>();
  const match = useRouteMatch();
  const location = useLocation();
  const pathname = location.pathname;

  const isServices = pathname === '/catalog/services';

  // ЭТАП 2 — banner/text/bigCards/breadCrumbs/popular/recipe теперь
  // ДЕРИВАТИВЫ query (см. ниже): рендерятся на SSR, setState-цикл убран.
  const [currentGroup, setCurrentGroup] = useState<
    CatalogTypeFiltersModel | undefined | null
  >(null);
  const [seoBlockInfo, setSeoBlockInfo] = useState<CatalogSeoBlockProps | null>(
    null
  );

  // ЭТАП 2 — баннер/текст/крошки рубрики через react-query: ключ зеркалится
  // SSR-префетчем (ssrPrefetch, pristine-значения), крошки и баннер приходят
  // в HTML первого ответа. xStoreId в ключе заменяет старую mobx-reaction
  // (смена магазина -> новый ключ -> перезапрос), смена настроек/URL — тоже.
  const bannerAndTextParams = id
    ? buildBannerAndTextKeyParams(pathname, id, {
        catalogSorting: userStore.settings.catalog_sorting,
        eppCatalog: userStore.settings.epp_catalog,
        xStoreId: xStore?.xStoreId ?? null
      })
    : null;
  const { data: bannerAndTextData, isPending: isBannerAndTextPending } =
    useQuery({
      ...bannerAndTextQueryOptions(
        bannerAndTextParams ?? { type: 'catalog', setOrder: 'popular' }
      ),
      enabled: Boolean(bannerAndTextParams)
    });

  // ЭТАП 2 — «Популярные товары» корня каталога (/catalog): раньше грузились
  // императивно в getMainInfo (setState в эффекте) — SSR не отдавал нижнюю
  // карусель. Factory общий с CatalogServices (indexProducts.ts); ключ
  // зеркалится префетчем (ssrPrefetch, xStoreId null).
  const { data: rootPopularItems, isPending: isRootPopularPending } = useQuery({
    ...popularMainPageItemsQueryOptions(xStore?.xStoreId ?? null),
    enabled: !id
  });

  // ЭТАП 2 — рецепт-рекомендация (/catalog/:id, блок «рецепт + SEO-текст»):
  // из getMainInfo → react-query, ключ зеркалится префетчем — HorizontalRecipe
  // попадает в SSR-HTML первого ответа.
  const { data: recipeData, isPending: isRecipePending } = useQuery({
    ...recipesRecommendQueryOptions({ xStoreId: xStore?.xStoreId ?? null }),
    enabled: Boolean(id) && !isServices
  });
  const recipe = recipeData ?? null;

  const bannerAndTextProducts = bannerAndTextData?.getProducts;
  const breadCrumbs = useMemo<BreadCrumbsT>(
    () =>
      bannerAndTextProducts?.rubricBreadcrumbs?.map(item => ({
        title: item.title || '',
        url: `/catalog/${item.identify}`
      })) || [],
    [bannerAndTextProducts]
  );
  const banner = bannerAndTextProducts?.rubricBanner || '';
  const text = bannerAndTextProducts?.rubricText || '';
  const isProductBigCards = !!bannerAndTextProducts?.rubricBigCards;
  // На категории популярные — из того же ответа bannerAndText (SSR);
  // на корне '/catalog' — запрос popularMainPageItems (тоже SSR).
  const popularItems = id
    ? bannerAndTextProducts?.popularCatalogItems ?? []
    : rootPopularItems ?? [];
  const isPopularSlotLoading = id
    ? isBannerAndTextPending
    : isRootPopularPending;

  const prevPathname = usePreviousValue(pathname);
  const prevId = usePreviousValue(id);

  useEffect(() => {
    if (!prevPathname) {
      return;
    }
    const urlParams = getURLProps({ pathname });
    const oldUrlParams = getURLProps({ pathname: prevPathname });
    // ЭТАП 2 — перезапросы при смене URL/магазина делает react-query
    // (параметры входят в queryKey); здесь остался только скролл наверх.
    if (
      prevId !== id ||
      urlParams.filter !== oldUrlParams.filter ||
      urlParams.type !== oldUrlParams.type ||
      urlParams.group !== oldUrlParams.group
    ) {
      scrollTo.params({ top: 0, behavior: 'smooth' });
    }
  }, [id, pathname, prevId, prevPathname]);

  // ЭТАП 2 — SSR-фолбэк крошки активной группы (/r/group/<identify>):
  // точный currentGroup приезжает из products-ответа через setState в эффекте
  // CatalogCategory (клиент), но НАЗВАНИЕ группы есть и в каталожном меню
  // (rubricGroup), которое SSR-ится (useCatalogMenu). Сервер и первый
  // клиентский рендер берут его из меню; заполнившийся currentGroup
  // перекрывает (тот же title — визуально без скачка).
  const catalogMenuForCrumbs = useCatalogMenu();
  const groupIdentifyFromUrl = getURLProps({ pathname }).group;
  const menuGroupTitle = useMemo<string | null>(() => {
    if (!groupIdentifyFromUrl) {
      return null;
    }
    for (const item of catalogMenuForCrumbs) {
      const found = item.rubricGroup?.find(
        gr => gr?.identify === groupIdentifyFromUrl
      );
      if (found?.title) {
        return found.title;
      }
    }
    return null;
  }, [catalogMenuForCrumbs, groupIdentifyFromUrl]);

  // ЭТАП 2 — SSR-фолбэк seo-блока: точный seoBlockInfo ставит эффект
  // useCatalogProducts (клиент, setState), но те же поля лежат в ПЕРВОЙ
  // странице products-запроса, который префетчится с pristine-ключом
  // (ssrPrefetch/catalogInitialParams). enabled:false — только чтение кэша,
  // сетевых запросов не добавляет; заполнившийся seoBlockInfo перекрывает
  // (значения те же — без скачка).
  const initialCatalogArgs = useMemo(
    () => buildInitialCatalogQueryArgs(pathname, location.search ?? ''),
    [pathname, location.search]
  );
  const { data: initialCatalogData } = useInfiniteQuery({
    ...catalogProductsInfiniteQueryOptions(
      initialCatalogArgs?.params ?? {
        slug: '',
        setOrder: 'popular',
        isExpress: false
      },
      initialCatalogArgs?.initialPage ?? 1
    ),
    enabled: false
  });
  const initialGetProducts = initialCatalogData?.pages?.[0]?.getProducts;
  const ssrSeoBlockInfo = useMemo<CatalogSeoBlockProps | null>(
    () =>
      initialGetProducts
        ? {
            categoryTitle: initialGetProducts.rubricTitle || '',
            minPrice: initialGetProducts.catalogSeo?.lowPrice || 0,
            totalProductsCount: initialGetProducts.totalCount || 0
          }
        : null,
    [initialGetProducts]
  );
  const effectiveSeoBlockInfo = seoBlockInfo ?? ssrSeoBlockInfo;

  const breadCrumbsArray = useMemo<BreadCrumbsT>(() => {
    const arr: BreadCrumbsT = [
      { title: 'Главная', url: '/' },
      { title: 'Каталог', url: '/catalog' },
      ...breadCrumbs
    ];
    if (currentGroup) {
      arr.push({ title: currentGroup.title || '', url: '#' });
    } else if (menuGroupTitle) {
      arr.push({ title: menuGroupTitle, url: '#' });
    }
    return arr;
  }, [breadCrumbs, currentGroup, menuGroupTitle]);

  let isShowLinkLastElement = false;
  if (breadCrumbs?.length > 0 && breadCrumbs?.[breadCrumbs.length - 1]) {
    isShowLinkLastElement =
      pathname.includes(`${breadCrumbs[breadCrumbs.length - 1].url}`) &&
      !!getURLProps({ pathname }).group;
  }

  const shouldRenderRecipeSection =
    match.path === '/catalog/:id' && !isServices;
  const isRecipeSectionReady = !!recipe?.id;

  const { data: catalogFaqData } = useQuery({
    ...catalogFaqQueryOptions(id ?? ''),
    enabled: shouldRenderRecipeSection && !!id
  });
  const faqItems = useMemo(
    () => selectCatalogFaqItems(catalogFaqData),
    [catalogFaqData]
  );
  const seoCard =
    !!id && (!!text || faqItems.length > 0) ? (
      <div className='relative h-[332px] '>
        <div className='pt-4 px-5 pb-8 max-h-full custom-scroll overflow-auto rounded-lg bg-[#fafafa]'>
          {!!text && (
            <SeoBottomTextWrapper
              className='sm:[&>h2]:text-lg sm:[&>h2]:leading-tight sm:[&>h2]:mb-3 [&>h2]:font-bold [&>p]:text-sm [&>p]:mb-4 [&>p:last-of-type]:mb-0 [&>p]:leading-tight [&>h2]:text-2.5xl [&>h2]:mb-5'
              dangerouslySetInnerHTML={{ __html: text }}
            />
          )}
          <CatalogFaq slug={id} items={faqItems} />
        </div>
        <div className='absolute -bottom-2.5 left-0 w-full h-0 pointer-events-none shadow-[0_-18px_10px_7px_#fafafa]' />
      </div>
    ) : null;

  return (
    <div className='bg-[#f6f5f7] w-full'>
      <div className='main-container catalog-page md:px-6.25'>
        {banner && (
          <div className='main-banner main-slider slim-main-banner'>
            <div className='slide'>
              <img src={banner} alt='Баннер' />
            </div>
            <div className='slider-control'>
              <input type='radio' id='slide_1' name='slide' />
              <label htmlFor='slide_1' />
              <input type='radio' id='slide_2' name='slide' checked />
              <label htmlFor='slide_2' />
              <input type='radio' id='slide_3' name='slide' />
              <label htmlFor='slide_3' />
            </div>
          </div>
        )}

        <Breadcrumbs
          noPaddingMobile
          withoutBackBtn
          array={breadCrumbsArray}
          isShowLinkLastElement={isShowLinkLastElement}
        />

        <Switch>
          <Route exact path='/catalog/' component={CatalogMain} />
          <Route exact path='/catalog/bar' component={CatalogBar} />
          {/* <Route path='/catalog/services' component={CatalogServices} /> */}
          <Route
            path='/catalog/:id'
            render={props => (
              <CatalogCategory
                {...props}
                setSeoBlockInfo={setSeoBlockInfo}
                setCurrentGroup={setCurrentGroup}
                isProductBigCards={isProductBigCards}
              />
            )}
          />
        </Switch>

        <Fragment>
          {!isServices && (
            <div className='catalog-popular-slot'>
              {popularItems?.length ? (
                <Carousel
                  title={'Популярные товары'}
                  products={popularItems}
                  withPaddingTablet={false}
                />
              ) : isPopularSlotLoading ? (
                <div
                  className='products-container products-container--placeholder'
                  aria-hidden='true'
                />
              ) : null}
            </div>
          )}

          {!isServices && effectiveSeoBlockInfo && (
            <div className='mb-8 sm:mb-10 md:mb-8'>
              <CatalogSeoBlock {...effectiveSeoBlockInfo} />
            </div>
          )}

          {shouldRenderRecipeSection && (
            <Fragment>
              <div
                className={twcls(
                  'recipes',
                  !isRecipeSectionReady && isRecipePending && 'recipes--loading'
                )}
              >
                {isRecipeSectionReady ? (
                  <div className='grid grid-cols-[740px,auto] gap-6 mt-10 h-[332px] allMd:grid-cols-1 allMd:h-auto sm:h-auto'>
                    <HorizontalRecipe recipe={recipe} />
                    {seoCard}
                  </div>
                ) : isRecipePending ? (
                  <div className='recipes__placeholder' aria-hidden='true' />
                ) : (
                  seoCard && <div className='mt-10'>{seoCard}</div>
                )}
              </div>
              <div className='clear' />
            </Fragment>
          )}
        </Fragment>
      </div>
    </div>
  );
});

export default Catalog;
